/**
 * BDL20-3995: the Fix SSCC XML pop-up's state and rules (V1–V14), ported from the Claude Design prototype
 * "Manage Order - SSCC XML" (analyse()). Pure functions, so the modal only renders what this returns.
 */

export interface FixHeader {
  messageNo: string
  customer_id: string
  order_number: string
  connote: string
  /** yyyy-MM-dd (native date input) */
  orderdate: string
  carrier: string
  order_currency_code: string
  /** yyyy-MM-dd (native date input) */
  date_shipped: string
}

export interface FixLine {
  id: number
  sku: string
  ordered: string
  shipped: string
  /** Pre-filled from SO Create: SKU and ordered quantity are not editable. */
  fromOrder: boolean
}

export interface FixCartonItem {
  id: number
  sku: string
  qty: string
}

export interface FixCarton {
  id: number
  label: string
  l: string
  w: string
  h: string
  kg: string
  items: FixCartonItem[]
}

export interface FixState {
  header: FixHeader
  lines: FixLine[]
  cartons: FixCarton[]
}

export const HEADER_FIELDS: {
  key: keyof FixHeader
  label: string
  kind: 'auto' | 'date' | 'autoDate'
}[] = [
  { key: 'messageNo', label: 'messageNo', kind: 'auto' },
  { key: 'customer_id', label: 'customer_id', kind: 'auto' },
  { key: 'order_number', label: 'order_number', kind: 'auto' },
  { key: 'connote', label: 'connote', kind: 'auto' },
  { key: 'orderdate', label: 'orderdate', kind: 'autoDate' },
  { key: 'carrier', label: 'carrier', kind: 'auto' },
  { key: 'order_currency_code', label: 'order_currency_code', kind: 'auto' },
  { key: 'date_shipped', label: 'date_shipped', kind: 'date' },
]

let uid = 0
export const nextId = () => {
  uid += 1
  return uid
}

export const num = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || String(v).trim() === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export const lineError = (line: FixLine) => {
  const shipped = String(line.shipped ?? '').trim()
  if (!shipped) return 'Required'
  const n = num(shipped)
  const ordered = num(line.ordered)
  if (n === null || n < 0 || !Number.isInteger(n))
    return 'Enter a valid quantity'
  if (ordered !== null && n > ordered)
    return 'Shipped quantity cannot exceed ordered quantity'
  return ''
}

export const qtyError = (qty: string) => {
  if (String(qty ?? '').trim() === '') return 'Required'
  const n = num(qty)
  if (n === null || n < 1 || !Number.isInteger(n)) return 'Min 1'
  return ''
}

export const dimError = (value: string) => {
  if (String(value ?? '').trim() === '') return 'Required'
  const n = num(value)
  if (n === null || n <= 0) return 'Cannot be zero'
  return ''
}

/** L × W × H (mm) → m³; 0 until all three are set. */
export const cartonCube = (c: FixCarton) => {
  const l = num(c.l)
  const w = num(c.w)
  const h = num(c.h)
  return l && w && h ? (l * w * h) / 1e9 : 0
}

export interface FixAnalysis {
  lineErrors: Record<number, string>
  qtyErrors: Record<number, string>
  labelErrors: Record<number, string>
  dimErrors: Record<number, Record<'l' | 'w' | 'h' | 'kg', string>>
  headerErrors: Partial<Record<keyof FixHeader, boolean>>
  shippedBySku: Record<string, number>
  allocBySku: Record<string, number>
  invalidSku: Record<string, boolean>
  totalWeight: number
  totalCube: number
  shippedTotal: number
  allocTotal: number
  reconOk: boolean
  issues: number
}

/**
 * Everything the screen shows as an error, plus the issue count that blocks Save.
 * `usedLabels` are Label IDs already used on other TWD orders (V7, known after a blocked save).
 */
export const analyse = (
  state: FixState,
  usedLabels: string[] = [],
): FixAnalysis => {
  const lineErrors: Record<number, string> = {}
  const shippedBySku: Record<string, number> = {}
  const invalidSku: Record<string, boolean> = {}
  state.lines.forEach((line) => {
    const e = lineError(line)
    lineErrors[line.id] = e
    if (!line.sku) return
    if (e) invalidSku[line.sku] = true
    else
      shippedBySku[line.sku] =
        (shippedBySku[line.sku] || 0) + (num(line.shipped) || 0)
  })

  const allocBySku: Record<string, number> = {}
  const qtyErrors: Record<number, string> = {}
  const labelErrors: Record<number, string> = {}
  const dimErrors: FixAnalysis['dimErrors'] = {}
  const used = usedLabels.map((l) => l.trim().toLowerCase())

  state.cartons.forEach((carton) => {
    carton.items.forEach((item) => {
      qtyErrors[item.id] = qtyError(item.qty)
      if (item.sku)
        allocBySku[item.sku] =
          (allocBySku[item.sku] || 0) + (num(item.qty) || 0)
    })
    const label = carton.label.trim()
    if (!label) labelErrors[carton.id] = 'Required'
    else if (used.includes(label.toLowerCase()))
      labelErrors[carton.id] = 'Label ID already used on another TWD order'
    else if (
      state.cartons.filter(
        (x) => x.label.trim().toLowerCase() === label.toLowerCase(),
      ).length > 1
    )
      labelErrors[carton.id] = 'Duplicate Label ID in this order'
    else labelErrors[carton.id] = ''
    dimErrors[carton.id] = {
      l: dimError(carton.l),
      w: dimError(carton.w),
      h: dimError(carton.h),
      kg: dimError(carton.kg),
    }
  })

  const headerErrors: FixAnalysis['headerErrors'] = {}
  HEADER_FIELDS.forEach((f) => {
    if (!String(state.header[f.key] ?? '').trim()) headerErrors[f.key] = true
  })

  const totalWeight = state.cartons.reduce((a, c) => a + (num(c.kg) || 0), 0)
  const totalCube = state.cartons.reduce((a, c) => a + cartonCube(c), 0)
  const shippedTotal = Object.values(shippedBySku).reduce((a, b) => a + b, 0)
  const allocTotal = Object.values(allocBySku).reduce((a, b) => a + b, 0)
  const skus = Object.keys({ ...shippedBySku, ...allocBySku })
  const reconOk =
    allocTotal > 0 &&
    skus.every((k) => (shippedBySku[k] || 0) === (allocBySku[k] || 0))

  let issues = Object.keys(headerErrors).length
  issues += Object.values(lineErrors).filter(Boolean).length
  issues += state.lines.filter((l) => !l.sku).length
  issues += Object.values(labelErrors).filter(Boolean).length
  issues += Object.values(qtyErrors).filter(Boolean).length
  issues += state.cartons.filter((c) => c.items.some((it) => !it.sku)).length
  Object.values(dimErrors).forEach((d) => {
    issues += Object.values(d).filter(Boolean).length
  })
  if (state.cartons.length === 0) issues += 1
  if (!reconOk) issues += 1

  return {
    lineErrors,
    qtyErrors,
    labelErrors,
    dimErrors,
    headerErrors,
    shippedBySku,
    allocBySku,
    invalidSku,
    totalWeight,
    totalCube,
    shippedTotal,
    allocTotal,
    reconOk,
    issues,
  }
}

export interface SkuOption {
  sku: string
  note: string
  disabled: boolean
}

/** The carton SKU dropdown for one row (V11–V13 + availability note). */
export const skuOptions = (
  state: FixState,
  a: FixAnalysis,
  carton: FixCarton,
  item: FixCartonItem,
): SkuOption[] => {
  const lineSkus = Array.from(
    new Set(state.lines.map((l) => l.sku).filter(Boolean)),
  )
  return lineSkus.map((sku) => {
    const others = state.cartons.reduce(
      (t, c) =>
        t +
        c.items
          .filter((y) => y.id !== item.id && y.sku === sku)
          .reduce((u, y) => u + (num(y.qty) || 0), 0),
      0,
    )
    const shipped = a.shippedBySku[sku] || 0
    const selected = sku === item.sku
    const inCarton =
      !selected && carton.items.some((y) => y.id !== item.id && y.sku === sku)
    const invalid = !!a.invalidSku[sku]
    const full = !selected && others >= shipped
    let note = `${Math.max(shipped - others, 0)} of ${shipped} available`
    if (inCarton) note = 'Already in this carton'
    else if (invalid) note = 'Shipped quantity needs fixing'
    else if (full) note = 'All units allocated'
    return { sku, note, disabled: !selected && (inCarton || invalid || full) }
  })
}

/** "+ Add SKU to carton" pre-selects the next SKU that still has unallocated units. */
export const nextSkuForCarton = (
  state: FixState,
  a: FixAnalysis,
  carton: FixCarton,
) => {
  const lineSkus = Array.from(
    new Set(state.lines.map((l) => l.sku).filter(Boolean)),
  )
  const free = lineSkus.filter((k) => !carton.items.some((y) => y.sku === k))
  return (
    free.find(
      (k) =>
        !a.invalidSku[k] && (a.shippedBySku[k] || 0) > (a.allocBySku[k] || 0),
    ) ||
    free[0] ||
    ''
  )
}
